"use client";

/**
 * The public pages' language switcher: each language by its own name, linking
 * to this same page in that language.
 *
 * Links, not a `<select>`: every language's page is a real URL a crawler can
 * follow and a reader can open in a new tab. Endonyms, never flags — a flag
 * names a country, and Spanish is not Spain's. Following one also remembers
 * the choice in the locale cookie, so the sign-up funnel the visitor enters
 * next (a Cloud Run page with no prefix) stays in the same language.
 *
 * Renders nothing when only one locale is live in this environment
 * (the `locales` feature).
 */
import type { Locale } from "@reflect/domain";
import { usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";

import { defaultLocalePath, localisedPath } from "../../lib/i18n/alternates";
import { LIVE_LOCALES } from "../../lib/i18n/liveLocales";
import { toLocale, writeLocaleCookie } from "../../lib/i18n/localeCookie";
import { languageEndonym } from "./languageEndonym";

/** Props for {@link LanguageSwitcher}. */
export interface LanguageSwitcherProps {
  /** Classes for the `<nav>` — the footers it sits in differ in colour. */
  className?: string;
  /** The locales on offer. Defaults to the registry; a seam for tests. */
  locales?: readonly Locale[];
}

/**
 * Render a link to this page in every other language — or nothing, without
 * touching any context, while there is only one. The homepage footer mounts it
 * unconditionally, including under tests that render no intl provider.
 *
 * @param props - see {@link LanguageSwitcherProps}.
 */
export function LanguageSwitcher({
  className,
  locales = LIVE_LOCALES,
}: LanguageSwitcherProps): React.JSX.Element | null {
  if (locales.length < 2) return null;
  return <LanguageLinks className={className} locales={locales} />;
}

/**
 * The switcher's links.
 *
 * @param props - the classes and the locales on offer (two or more).
 */
function LanguageLinks({
  className,
  locales,
}: {
  className: string | undefined;
  locales: readonly Locale[];
}): React.JSX.Element {
  const t = useTranslations("web.language");
  const current = toLocale(useLocale());
  const path = defaultLocalePath(usePathname() ?? "/", locales);
  return (
    <nav aria-label={t("switcherLabel")} data-test-id="language-switcher" className={className}>
      {locales.map((locale, index) => (
        <span key={locale}>
          {index > 0 && " · "}
          {locale === current ? (
            <span lang={locale} aria-current="page">
              {languageEndonym(locale)}
            </span>
          ) : (
            <a
              href={localisedPath(path, locale)}
              hrefLang={locale}
              lang={locale}
              data-test-id={`language-switcher-${locale}`}
              onClick={() => writeLocaleCookie(locale)}
              style={{ color: "inherit", textDecoration: "underline" }}
            >
              {languageEndonym(locale)}
            </a>
          )}
        </span>
      ))}
    </nav>
  );
}
